
import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from "@/components/ui/toaster";
import { AuthProvider, useAuth } from '@/contexts/auth';
import LoadingScreen from '@/components/ui/LoadingScreen';

// Lazy load pages
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const WorkflowBuilder = lazy(() => import('@/pages/WorkflowBuilder'));
const CreateWorkflow = lazy(() => import('@/pages/CreateWorkflow'));
const EditWorkflow = lazy(() => import('@/pages/EditWorkflow'));
const Settings = lazy(() => import('@/pages/Settings'));
const InApp = lazy(() => import('@/pages/InApp'));
const Analytics = lazy(() => import('@/pages/Analytics'));
const FeedbackResponses = lazy(() => import('@/pages/FeedbackResponses'));
const SurveyPreview = lazy(() => import('@/pages/SurveyPreview'));
const RecoveryForm = lazy(() => import('@/pages/RecoveryForm'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const MetricsAnalysis = lazy(() => import('@/pages/MetricsAnalysis'));
const FeedbackFormPreview = lazy(() => import('@/pages/FeedbackFormPreview'));

// Protected route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public routes that redirect to dashboard if user is logged in
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            
            {/* Public Survey routes - no authentication required */}
            <Route path="/survey-preview" element={<SurveyPreview />} />
            <Route path="/recovery-form" element={<RecoveryForm />} />
            
            {/* Protected routes */}
            <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/dashboard" element={<Navigate to="/" replace />} />
            <Route path="/workflow-builder" element={<ProtectedRoute><WorkflowBuilder /></ProtectedRoute>} />
            <Route path="/workflows" element={<Navigate to="/workflow-builder" replace />} />
            <Route path="/workflows/create" element={<ProtectedRoute><CreateWorkflow /></ProtectedRoute>} />
            <Route path="/workflows/edit/:id" element={<ProtectedRoute><EditWorkflow /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/in-app" element={<ProtectedRoute><InApp /></ProtectedRoute>} />
            <Route path="/analysis" element={<ProtectedRoute><MetricsAnalysis /></ProtectedRoute>} />
            <Route path="/feedback-responses" element={<ProtectedRoute><FeedbackResponses /></ProtectedRoute>} />
            <Route path="/feedback-form-preview" element={<ProtectedRoute><FeedbackFormPreview /></ProtectedRoute>} />
            <Route path="/feedback-preview" element={<Navigate to="/feedback-form-preview" replace />} />
            
            {/* 404 route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Router>
      <Toaster />
    </AuthProvider>
  );
}

export default App;
